import React from 'react'
import { I18nContext, I18nContextValue, TranslationKey } from '@structura/i18n'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// English last-resort labels: a class error boundary cannot use the useTranslation
// hook, and it must never throw, so it falls back to these if the i18n context is
// somehow absent (it is rendered inside I18nProvider, so normally t() is used).
const FALLBACK: Record<string, string> = {
  'ui.errorBoundary.title': 'Something went wrong',
  'ui.errorBoundary.errorLabel': 'Error:',
  'ui.errorBoundary.stackLabel': 'Stack:',
  'ui.errorBoundary.reload': 'Reload Page'
}

class ErrorBoundary extends React.Component<Props, State> {
  static contextType = I18nContext
  declare context: I18nContextValue | null

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo)
  }

  private label(key: TranslationKey & keyof typeof FALLBACK): string {
    return this.context ? this.context.t(key) : FALLBACK[key]
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger">
            <h4 className="alert-heading">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {this.label('ui.errorBoundary.title')}
            </h4>
            <p><strong>{this.label('ui.errorBoundary.errorLabel')}</strong> {this.state.error?.message}</p>
            <hr />
            <p className="mb-0">
              <strong>{this.label('ui.errorBoundary.stackLabel')}</strong>
              <pre style={{ fontSize: '0.8rem', maxHeight: '300px', overflow: 'auto' }}>
                {this.state.error?.stack}
              </pre>
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => window.location.reload()}
            >
              {this.label('ui.errorBoundary.reload')}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

